import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { RAIL_CLOSE, RAIL_OPEN } from "../railEvents";
import PatientDetailsForm from "./PatientDetailsForm";
import { PD_V2_CLOSE, PD_V2_EDIT } from "./events";
import { overlayCapturedOnPatient } from "../aiTranscript/patientDetailsBodyAdapters";
import { useSheetCountRegistration } from "../../components/sheetCounts";

/**
 * Patient details editor rail — the right column when a table row
 * click has opened the form.
 *
 * Same shell contract every editorOnDemand section follows:
 *   • listen for the section's OPEN event (PD_V2_EDIT) and stash the
 *     opener's payload (`focusField`) in local state
 *   • toggle a boolean; emit RAIL_OPEN / RAIL_CLOSE so PatientShell
 *     shows / hides the 340px column at the right times
 *   • render the form once open; unmount it on close so the form's
 *     internal state is fresh next time
 *   • surface a PD_V2_CLOSE broadcast so a saver (or the header
 *     dialog) can programmatically shut this rail down without
 *     touching internal state
 *
 * Only one editing session exists at any moment — clicking a second
 * row while the rail is open just replaces the focus target,
 * matching the dialog's single-instance model.
 */
const PatientDetailsEditor = ({
  patient,
  onManualSave,
  onReject,
  capturedItems,
  open: openProp,
  focusField: focusFieldProp,
  focusAt: focusAtProp,
  onClose: onCloseProp,
  themeClass,
  // ZY-10356 Phase 1 — transcript-mode draft preservation. Scoped to
  // the transcript by including transcriptId in the key (passed from
  // CapturedSections). Standalone leaves undefined → form hook no-ops.
  draftKey,
}) => {
  // Controlled vs uncontrolled. Standalone mounts leave the *Prop
  // pairs undefined and the internal event-bus listener drives state.
  // Transcript mode passes both open and onClose so CapturedSections
  // owns the state directly — the event bus is bypassed for that
  // path since the direct callback is more predictable across the
  // modal / shell double-mount scenario.
  const isControlled = typeof openProp === "boolean";

  const [openInternal, setOpenInternal] = useState(false);
  const [focusFieldInternal, setFocusFieldInternal] = useState(null);
  const [focusAtInternal, setFocusAtInternal] = useState(0);

  // Publish this editor's open-card count into the shell registry so the
  // sidebar badge + leave-page warning see Patient details alongside the
  // ten useSheetStack-based sections (ZY-10350). Scoped to the SHELL
  // (uncontrolled) mount via a null sectionKey when controlled — the
  // transcript-modal mount shouldn't register because the shell doesn't
  // own its lifecycle and would race with its own mount's writes.
  useSheetCountRegistration(
    isControlled ? null : "patient-details",
    openInternal ? 1 : 0,
  );

  const open = isControlled ? openProp : openInternal;
  const focusField = isControlled ? focusFieldProp || null : focusFieldInternal;
  const focusAt = isControlled ? focusAtProp || 0 : focusAtInternal;

  // Transcript mode passes captured items so the form seeds from the
  // STAGED overlay, not the raw record. Only one captured update ever
  // exists for patient_details (single-row section — see the body
  // adapter's uniqueKey convention).
  const stagedCaptured = useMemo(() => {
    if (!Array.isArray(capturedItems)) return null;
    return capturedItems.find((it) => (it && it.state) !== "reviewed") || null;
  }, [capturedItems]);
  const effectivePatient = useMemo(() => {
    if (!stagedCaptured) return patient;
    // ZY-10387 Shift 2 — overlay from `effective` (= LastCommitted ∪
    // Current, nulls stripped server-side) NOT `current`. Patient
    // details' ProposeCaptured merge REPLACES Current with each AI
    // delta (`it.Current = req.Current`), so a thin AI re-emit after
    // the reviewer committed firstName="Alex" would leave Current =
    // {gender: 1} only — and overlaying that onto `patient` would
    // display the ORIGINAL DB firstName, hiding the reviewer's commit.
    // Effective preserves the committed fields underneath the AI delta.
    const payload = stagedCaptured.effective
      || stagedCaptured.lastCommitted
      || stagedCaptured.current
      || null;
    if (!payload) return patient;
    return overlayCapturedOnPatient(patient, payload);
  }, [patient, stagedCaptured]);

  const close = useCallback(() => {
    if (typeof onCloseProp === "function") {
      onCloseProp();
    } else {
      setOpenInternal(false);
      setFocusFieldInternal(null);
    }
    EventService.emit(RAIL_CLOSE);
  }, [onCloseProp]);

  useEffect(() => {
    // Skip the event bus entirely in controlled mode — the parent
    // owns state and any stray PD_V2_EDIT emit would just re-open a
    // rail we're already managing.
    if (isControlled) return undefined;
    const onEdit = (payload) => {
      setFocusFieldInternal((payload && payload.focusField) || null);
      setFocusAtInternal((payload && payload.focusAt) || 0);
      setOpenInternal(true);
      EventService.emit(RAIL_OPEN);
    };
    EventService.addListener(PD_V2_EDIT, onEdit);
    EventService.addListener(PD_V2_CLOSE, close);
    return () => {
      EventService.removeListener(PD_V2_EDIT, onEdit);
      EventService.removeListener(PD_V2_CLOSE, close);
      // Ensure the shell doesn't leave the rail pinned open when
      // the reviewer navigates away mid-edit.
      EventService.emit(RAIL_CLOSE);
    };
  }, [close, isControlled]);

  // Payload-hashed key on PatientDetailsForm so a mid-review AI
  // re-emit (parent re-renders with a new `effectivePatient`) forces
  // an unmount + remount — the form's useState initializers only run
  // on mount, so without this the open card would freeze at the
  // first-mount seed values even after the agent merged a new field
  // update. Same mechanism Addictions / Anthropometric use.
  // IMPORTANT: this useMemo must run on every render (never
  // conditionally) — the `if (!open) return null` early-exit below
  // would otherwise violate the Rules of Hooks.
  const payloadHash = useMemo(() => {
    const c = stagedCaptured && (stagedCaptured.effective
      || stagedCaptured.lastCommitted
      || stagedCaptured.current);
    return c ? JSON.stringify(c) : "";
  }, [stagedCaptured]);

  if (!open) return null;

  return (
    <PatientDetailsForm
      key={`pd::${payloadHash}`}
      patient={effectivePatient}
      focusField={focusField}
      focusAt={focusAt}
      onClose={close}
      onSaved={close}
      onManualSave={onManualSave}
      onReject={onReject}
      themeClass={themeClass}
      /* Scope the draft bucket to the current payload too so a fresh
         AI re-emit doesn't replay the previous mention's typed state
         over the new seed. Mirrors the Addictions / Anthropometric
         scoped formId pattern. */
      draftKey={draftKey ? `${draftKey}::${payloadHash}` : draftKey}
    />
  );
};

PatientDetailsEditor.propTypes = {
  patient: PropTypes.object.isRequired,
  /** Transcript mode: routes form save through /captured instead of bridge. */
  onManualSave: PropTypes.func,
  /** Transcript mode: ghost-button Reject (server soft-delete of the
   *  captured). When wired, closes rail + reverts the table to DB
   *  values. */
  onReject: PropTypes.func,
  /** Transcript mode: staged captured items for the patient_details section. */
  capturedItems: PropTypes.arrayOf(PropTypes.object),
  /** Controlled-mode open flag. When boolean, the event bus is
   *  bypassed and the parent owns state. Standalone leaves undefined. */
  open: PropTypes.bool,
  /** Controlled-mode focus target. See PatientDetailsForm.focusField. */
  focusField: PropTypes.string,
  /** Controlled-mode click token — changes on every row click, so asking for
      the SAME field twice still moves the focus. */
  focusAt: PropTypes.number,
  /** ZY-10356 — transcript-mode draft preservation key (scoped to
   *  transcriptId by CapturedSections). Standalone omits. */
  draftKey: PropTypes.string,
  /** Controlled-mode close handler. Called on Cancel + successful Save. */
  onClose: PropTypes.func,
  /** Passed through to the form root — "is-ai" for maroon theming. */
  themeClass: PropTypes.string,
};

PatientDetailsEditor.defaultProps = {
  onManualSave: null,
  capturedItems: null,
  open: undefined,
  focusField: null,
  focusAt: 0,
  onClose: null,
  themeClass: "",
};

export default PatientDetailsEditor;
